import { useEffect, useMemo, useState } from "react";
import { cafeteriaClient } from "../apiClient/cafeteriaClient";
import { ApiError } from "../../platform/apiClient/apiRequest";
import { NotifyAllControl, NotifyOneControl } from "../../platform/components/NotifyControls";
import { formatEgp, parseEgpInput } from "../utils/money";
import { MonthlySummary } from "../components/MonthlySummary";
import type { CafeteriaCouponSummary, CafeteriaMenuItem, CafeteriaTransaction, CafeteriaWallet } from "../types/cafeteria.types";

type AdminTab = "menu" | "topup" | "coupons" | "transactions" | "summary";

const TABS: { id: AdminTab; label: string }[] = [
  { id: "menu", label: "Menu" },
  { id: "topup", label: "Top up" },
  { id: "coupons", label: "Coupons" },
  { id: "transactions", label: "Transactions" },
  { id: "summary", label: "Monthly summary" },
];

function MenuItemRow({ item, onChanged }: { item: CafeteriaMenuItem; onChanged: () => void }) {
  const [name, setName] = useState(item.name);
  const [priceText, setPriceText] = useState(item.price.toFixed(2));
  const [category, setCategory] = useState(item.category);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parsedPrice = parseEgpInput(priceText);
  const isDirty = name.trim() !== item.name || parsedPrice !== item.price || category.trim() !== item.category;

  async function handleSave() {
    const trimmedName = name.trim();
    const trimmedCategory = category.trim();
    if (!trimmedName) {
      setError("Name can't be empty.");
      return;
    }
    if (parsedPrice === null) {
      setError("Enter a valid price, e.g. 12.50.");
      return;
    }
    if (!trimmedCategory) {
      setError("Category can't be empty.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await cafeteriaClient.updateMenuItem(item.id, { name: trimmedName, price: parsedPrice, category: trimmedCategory });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't save this.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleToggleActive() {
    setSubmitting(true);
    setError(null);
    try {
      await cafeteriaClient.updateMenuItem(item.id, { isActive: !item.isActive });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't update this.");
      setSubmitting(false);
    }
  }

  async function handleDelete() {
    setSubmitting(true);
    setError(null);
    try {
      await cafeteriaClient.deleteMenuItem(item.id);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't delete this.");
      setSubmitting(false);
      setConfirmingDelete(false);
    }
  }

  return (
    <li className="about-admin-row">
      <div className="form-field">
        <label>Name</label>
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} disabled={submitting} />
      </div>
      <div className="form-field">
        <label>Price (EGP)</label>
        <input type="text" value={priceText} onChange={(e) => setPriceText(e.target.value)} disabled={submitting} />
      </div>
      <div className="form-field">
        <label>Category</label>
        <input type="text" list="cafeteria-menu-categories" value={category} onChange={(e) => setCategory(e.target.value)} disabled={submitting} />
      </div>
      <div className="about-admin-row-actions">
        {confirmingDelete ? (
          <div className="cancel-confirm">
            <span>Delete?</span>
            <button type="button" className="btn btn-ghost" onClick={() => setConfirmingDelete(false)} disabled={submitting}>
              No
            </button>
            <button type="button" className="btn btn-danger" onClick={handleDelete} disabled={submitting}>
              {submitting ? "Deleting…" : "Yes"}
            </button>
          </div>
        ) : (
          <>
            <button type="button" className="btn btn-primary" onClick={handleSave} disabled={submitting || !isDirty}>
              {submitting ? "Saving…" : "Save"}
            </button>
            <button type="button" className="btn btn-ghost" onClick={handleToggleActive} disabled={submitting}>
              {item.isActive ? "Deactivate" : "Reactivate"}
            </button>
            <button type="button" className="btn btn-danger-ghost" onClick={() => setConfirmingDelete(true)} disabled={submitting}>
              Delete
            </button>
          </>
        )}
      </div>
      {!item.isActive && <span className="status-badge status-badge--no-show">Inactive</span>}
      {error && <span className="field-error">{error}</span>}
    </li>
  );
}

function MenuSection() {
  const [items, setItems] = useState<CafeteriaMenuItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newName, setNewName] = useState("");
  const [newPriceText, setNewPriceText] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const [query, setQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  function refresh() {
    setIsLoading(true);
    setError(null);
    cafeteriaClient
      .getAdminMenu()
      .then(setItems)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load the menu."))
      .finally(() => setIsLoading(false));
  }

  useEffect(refresh, []);

  const categories = useMemo(() => [...new Set(items.map((i) => i.category).filter(Boolean))].sort(), [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((item) => {
      if (categoryFilter && item.category !== categoryFilter) return false;
      if (!q) return true;
      return item.name.toLowerCase().includes(q) || item.category.toLowerCase().includes(q);
    });
  }, [items, query, categoryFilter]);

  // Items already come back sorted category-then-name from the API, so consecutive runs
  // of the same category can just be grouped in a single pass.
  const groups = useMemo(() => {
    const out: { category: string; items: CafeteriaMenuItem[] }[] = [];
    for (const item of filtered) {
      const last = out[out.length - 1];
      if (last && last.category === item.category) last.items.push(item);
      else out.push({ category: item.category, items: [item] });
    }
    return out;
  }, [filtered]);

  async function handleAdd() {
    const trimmedName = newName.trim();
    const trimmedCategory = newCategory.trim();
    const price = parseEgpInput(newPriceText);
    if (!trimmedName) {
      setAddError("Provide a name first.");
      return;
    }
    if (price === null) {
      setAddError("Enter a valid price, e.g. 12.50.");
      return;
    }
    if (!trimmedCategory) {
      setAddError("Provide a category, e.g. \"Sandwiches\".");
      return;
    }
    setAdding(true);
    setAddError(null);
    try {
      await cafeteriaClient.createMenuItem({ name: trimmedName, price, category: trimmedCategory });
      setNewName("");
      setNewPriceText("");
      setNewCategory("");
      refresh();
    } catch (err) {
      setAddError(err instanceof ApiError ? err.message : "Couldn't add this item.");
    } finally {
      setAdding(false);
    }
  }

  return (
    <>
      <datalist id="cafeteria-menu-categories">
        {categories.map((c) => (
          <option key={c} value={c} />
        ))}
      </datalist>

      <div className="form-card">
        <h2>Add a menu item</h2>
        <div className="form">
          <div className="form-field">
            <label>Name</label>
            <input type="text" placeholder="e.g. Chicken sandwich" value={newName} onChange={(e) => setNewName(e.target.value)} />
          </div>
          <div className="form-field">
            <label>Price (EGP)</label>
            <input type="text" placeholder="e.g. 45.00" value={newPriceText} onChange={(e) => setNewPriceText(e.target.value)} />
          </div>
          <div className="form-field">
            <label>Category</label>
            <input
              type="text"
              list="cafeteria-menu-categories"
              placeholder="e.g. Sandwiches"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
            />
          </div>
          {addError && <div className="form-banner form-banner--error">{addError}</div>}
          <div className="form-actions">
            <button type="button" className="btn btn-primary" onClick={handleAdd} disabled={adding}>
              {adding ? "Adding…" : "Add item"}
            </button>
          </div>
        </div>
      </div>

      <h2 className="admin-section-heading">Menu items</h2>
      {error ? (
        <div className="state-panel">
          <p>{error}</p>
        </div>
      ) : isLoading ? (
        <div className="state-panel">Loading…</div>
      ) : items.length === 0 ? (
        <div className="state-panel">
          <p>No menu items yet — add the first one above.</p>
        </div>
      ) : (
        <>
          <div className="cafeteria-menu-filters">
            <input
              type="text"
              className="coach-search"
              placeholder="Search by name or category…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search menu items"
            />
            <select className="coach-search" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} aria-label="Filter by category">
              <option value="">All categories</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          {groups.length === 0 ? (
            <div className="state-panel">
              <p>No items match.</p>
            </div>
          ) : (
            groups.map((group) => (
              <div key={group.category}>
                <h3 className="cafeteria-menu-category-heading">{group.category || "Uncategorized"}</h3>
                <ul className="about-admin-list">
                  {group.items.map((item) => (
                    <MenuItemRow key={item.id} item={item} onChanged={refresh} />
                  ))}
                </ul>
              </div>
            ))
          )}
        </>
      )}
    </>
  );
}

function WalletTopUpRow({ wallet, onChanged }: { wallet: CafeteriaWallet; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [amountText, setAmountText] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function handleTopUp() {
    const amount = parseEgpInput(amountText);
    if (amount === null || amount <= 0) {
      setError("Enter a valid amount, e.g. 100.00.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await cafeteriaClient.topUpWallet(wallet.id, amount, note.trim() || undefined);
      setDone(true);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't top up this wallet.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <li className="about-admin-row">
      <div className="cafeteria-wallet-row-summary">
        <span>{wallet.email}</span>
        <span className="wallet-balance-inline">{formatEgp(wallet.balance)}</span>
        <button type="button" className="btn btn-ghost" onClick={() => setOpen((o) => !o)}>
          {open ? "Cancel" : "Top up"}
        </button>
      </div>
      {open && !done && (
        <div className="form">
          <div className="form-field">
            <label>Amount (EGP)</label>
            <input type="text" placeholder="e.g. 100.00" value={amountText} onChange={(e) => setAmountText(e.target.value)} disabled={submitting} />
          </div>
          <div className="form-field">
            <label>Note (optional)</label>
            <input type="text" placeholder="e.g. Manual top-up" value={note} onChange={(e) => setNote(e.target.value)} disabled={submitting} />
          </div>
          {error && <div className="form-banner form-banner--error">{error}</div>}
          <div className="form-actions">
            <button type="button" className="btn btn-primary" onClick={handleTopUp} disabled={submitting}>
              {submitting ? "Topping up…" : "Confirm top-up"}
            </button>
          </div>
        </div>
      )}
      {done && (
        <>
          <p className="field-hint">Topped up — new balance {formatEgp(wallet.balance)}.</p>
          <NotifyOneControl
            email={wallet.email}
            subjectDefault="Your cafeteria wallet was topped up"
            messageDefault={`Your cafeteria wallet was topped up by ${amountText} EGP. New balance: ${formatEgp(wallet.balance)}.`}
          />
        </>
      )}
    </li>
  );
}

function TopUpSection() {
  const [wallets, setWallets] = useState<CafeteriaWallet[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  const [allAmountText, setAllAmountText] = useState("");
  const [allNote, setAllNote] = useState("");
  const [confirmingAll, setConfirmingAll] = useState(false);
  const [submittingAll, setSubmittingAll] = useState(false);
  const [allError, setAllError] = useState<string | null>(null);
  const [allResult, setAllResult] = useState<number | null>(null);
  const [lastAllAmount, setLastAllAmount] = useState<number | null>(null);

  function refresh() {
    setIsLoading(true);
    setError(null);
    cafeteriaClient
      .getAdminWallets()
      .then(setWallets)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load wallets."))
      .finally(() => setIsLoading(false));
  }

  useEffect(refresh, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return wallets;
    return wallets.filter((w) => w.email.toLowerCase().includes(q));
  }, [wallets, query]);

  async function handleTopUpAll() {
    const amount = parseEgpInput(allAmountText);
    if (amount === null || amount <= 0) {
      setAllError("Enter a valid amount, e.g. 100.00.");
      return;
    }
    setSubmittingAll(true);
    setAllError(null);
    try {
      const result = await cafeteriaClient.topUpAll(amount, allNote.trim() || undefined);
      setAllResult(result.walletsUpdated);
      setLastAllAmount(amount);
      setConfirmingAll(false);
      setAllAmountText("");
      setAllNote("");
      refresh();
    } catch (err) {
      setAllError(err instanceof ApiError ? err.message : "Couldn't top up all wallets.");
    } finally {
      setSubmittingAll(false);
    }
  }

  return (
    <>
      <div className="cafeteria-topup-all-card">
        <h2>Top up ALL employees</h2>
        <p className="page-sub">Credits every wallet in the system by the same amount — e.g. a monthly allowance.</p>
        <div className="form">
          <div className="form-field">
            <label>Amount per employee (EGP)</label>
            <input
              type="text"
              placeholder="e.g. 500.00"
              value={allAmountText}
              onChange={(e) => setAllAmountText(e.target.value)}
              disabled={submittingAll}
            />
          </div>
          <div className="form-field">
            <label>Note (optional)</label>
            <input
              type="text"
              placeholder="e.g. September allowance"
              value={allNote}
              onChange={(e) => setAllNote(e.target.value)}
              disabled={submittingAll}
            />
          </div>
          {allError && <div className="form-banner form-banner--error">{allError}</div>}
          {allResult !== null && (
            <>
              <div className="form-banner">Topped up {allResult} wallet(s).</div>
              <NotifyAllControl
                subjectDefault="Your cafeteria wallet was topped up"
                messageDefault={`Your cafeteria wallet was topped up by ${lastAllAmount?.toFixed(2)} EGP.`}
              />
            </>
          )}
          <div className="form-actions">
            {confirmingAll ? (
              <div className="cancel-confirm">
                <span>
                  This credits all {wallets.length} wallet(s). You can delete it from Transactions if it's a mistake, but not once
                  anyone's spent part of it. Sure?
                </span>
                <button type="button" className="btn btn-ghost" onClick={() => setConfirmingAll(false)} disabled={submittingAll}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={handleTopUpAll} disabled={submittingAll}>
                  {submittingAll ? "Topping up…" : "Yes, top up all"}
                </button>
              </div>
            ) : (
              <button type="button" className="btn btn-primary" onClick={() => setConfirmingAll(true)}>
                Top up all employees
              </button>
            )}
          </div>
        </div>
      </div>

      <h2 className="admin-section-heading">Top up a single employee</h2>
      <input
        type="text"
        className="coach-search"
        placeholder="Search by email…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        aria-label="Search wallets by email"
      />
      {error ? (
        <div className="state-panel">
          <p>{error}</p>
        </div>
      ) : isLoading && wallets.length === 0 ? (
        // Only the very first load shows this — a refresh triggered by topping someone up
        // must NOT swap the list out (and unmount every WalletTopUpRow, wiping its "done"
        // state) while it re-fetches balances in the background.
        <div className="state-panel">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="state-panel">
          <p>No wallets match.</p>
        </div>
      ) : (
        <ul className="about-admin-list">
          {filtered.map((w) => (
            <WalletTopUpRow key={w.id} wallet={w} onChanged={refresh} />
          ))}
        </ul>
      )}
    </>
  );
}

function WalletCouponRow({ wallet, onGiven }: { wallet: CafeteriaWallet; onGiven: () => void }) {
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function handleGive() {
    const trimmed = description.trim();
    if (!trimmed) {
      setError("Describe the coupon first, e.g. \"Free meal — welcome!\".");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await cafeteriaClient.giveCoupon(wallet.id, trimmed, expiresAt || undefined);
      setDone(true);
      onGiven();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't give this coupon.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <li className="about-admin-row">
      <div className="cafeteria-wallet-row-summary">
        <span>{wallet.email}</span>
        <button type="button" className="btn btn-ghost" onClick={() => setOpen((o) => !o)}>
          {open ? "Cancel" : "Give coupon"}
        </button>
      </div>
      {open && !done && (
        <div className="form">
          <div className="form-field">
            <label>Description</label>
            <input
              type="text"
              placeholder="e.g. Free meal — welcome!"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={submitting}
            />
          </div>
          <div className="form-field">
            <label>Expires on (optional)</label>
            <input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} disabled={submitting} />
            <span className="field-hint">Leave blank for no expiry.</span>
          </div>
          {error && <div className="form-banner form-banner--error">{error}</div>}
          <div className="form-actions">
            <button type="button" className="btn btn-primary" onClick={handleGive} disabled={submitting}>
              {submitting ? "Giving…" : "Give coupon"}
            </button>
          </div>
        </div>
      )}
      {done && (
        <>
          <p className="field-hint">Given — one free meal is now waiting on their wallet page.</p>
          <NotifyOneControl
            email={wallet.email}
            subjectDefault="You have a free meal waiting"
            messageDefault={`You've been given a free meal coupon — ${description}. Show your coupon QR code at the cafeteria counter to redeem it.`}
          />
        </>
      )}
    </li>
  );
}

function IssuedCouponRow({ coupon, onChanged }: { coupon: CafeteriaCouponSummary; onChanged: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pendingCount = coupon.issuedCount - coupon.redeemedCount;
  const canDelete = pendingCount > 0;

  async function handleDelete() {
    setDeleting(true);
    setError(null);
    try {
      await cafeteriaClient.deleteCoupon(coupon.id);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't delete this coupon.");
      setDeleting(false);
      setConfirming(false);
    }
  }

  return (
    <li className="about-admin-row">
      <div className="cafeteria-wallet-row-summary">
        <span>{coupon.description}</span>
        <span className="field-hint">
          {coupon.issuedCount === 1 ? coupon.singleWalletEmail ?? "1 employee" : `${coupon.issuedCount} employees`} ·{" "}
          {coupon.redeemedCount} redeemed
          {coupon.expiresAt && ` · expires ${coupon.expiresAt}`}
        </span>
      </div>
      <div className="about-admin-row-actions">
        {confirming ? (
          <div className="cancel-confirm cancel-confirm--stacked">
            <span>Cancels {pendingCount === 1 ? "the 1 unredeemed slot" : `all ${pendingCount} unredeemed slots`}. Sure?</span>
            <button type="button" className="btn btn-ghost" onClick={() => setConfirming(false)} disabled={deleting}>
              No
            </button>
            <button type="button" className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? "Deleting…" : "Yes"}
            </button>
          </div>
        ) : canDelete ? (
          <button type="button" className="btn btn-danger-ghost" onClick={() => setConfirming(true)}>
            Delete
          </button>
        ) : (
          <span className="field-hint">Fully redeemed</span>
        )}
      </div>
      {error && <span className="field-error">{error}</span>}
    </li>
  );
}

function CouponSection() {
  const [description, setDescription] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ walletsIssued: number } | null>(null);
  const [lastDescription, setLastDescription] = useState("");

  const [wallets, setWallets] = useState<CafeteriaWallet[]>([]);
  const [walletsLoading, setWalletsLoading] = useState(true);
  const [walletsError, setWalletsError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  // Collapsed by default — the full wallet list can run into the hundreds and otherwise
  // buries "Coupons issued" below it.
  const [showEmployeeList, setShowEmployeeList] = useState(false);

  const [coupons, setCoupons] = useState<CafeteriaCouponSummary[]>([]);
  const [couponsLoading, setCouponsLoading] = useState(true);
  const [couponsError, setCouponsError] = useState<string | null>(null);

  useEffect(() => {
    cafeteriaClient
      .getAdminWallets()
      .then(setWallets)
      .catch((err) => setWalletsError(err instanceof Error ? err.message : "Failed to load wallets."))
      .finally(() => setWalletsLoading(false));
  }, []);

  function refreshCoupons() {
    setCouponsLoading(true);
    setCouponsError(null);
    cafeteriaClient
      .getAdminCoupons()
      .then(setCoupons)
      .catch((err) => setCouponsError(err instanceof Error ? err.message : "Failed to load coupons."))
      .finally(() => setCouponsLoading(false));
  }

  useEffect(refreshCoupons, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return wallets;
    return wallets.filter((w) => w.email.toLowerCase().includes(q));
  }, [wallets, query]);

  async function handleIssue() {
    const trimmed = description.trim();
    if (!trimmed) {
      setError("Describe the coupon first, e.g. \"Free meal — September 2026\".");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const created = await cafeteriaClient.createCoupon(trimmed, expiresAt || undefined);
      setResult({ walletsIssued: created.walletsIssued });
      setLastDescription(trimmed);
      setConfirming(false);
      setDescription("");
      setExpiresAt("");
      refreshCoupons();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't issue this coupon.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <div className="cafeteria-topup-all-card">
        <h2>Issue a free-meal coupon to ALL employees</h2>
        <p className="page-sub">
          Every employee gets one redemption, good for exactly one menu item at the counter (any price) — used once, then gone.
        </p>
        <div className="form">
          <div className="form-field">
            <label>Description</label>
            <input
              type="text"
              placeholder="e.g. Free meal — September 2026"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={submitting}
            />
          </div>
          <div className="form-field">
            <label>Expires on (optional)</label>
            <input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} disabled={submitting} />
            <span className="field-hint">Leave blank for no expiry.</span>
          </div>
          {error && <div className="form-banner form-banner--error">{error}</div>}
          {result && (
            <>
              <div className="form-banner">Issued to {result.walletsIssued} wallet(s).</div>
              <NotifyAllControl
                subjectDefault="You have a free meal waiting"
                messageDefault={`You've been given a free meal coupon — ${lastDescription}. Show your coupon QR code at the cafeteria counter to redeem it.`}
              />
            </>
          )}
          <div className="form-actions">
            {confirming ? (
              <div className="cancel-confirm">
                <span>This gives every employee a free meal. You can cancel unused ones later, but not once someone's redeemed it. Sure?</span>
                <button type="button" className="btn btn-ghost" onClick={() => setConfirming(false)} disabled={submitting}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={handleIssue} disabled={submitting}>
                  {submitting ? "Issuing…" : "Yes, issue to everyone"}
                </button>
              </div>
            ) : (
              <button type="button" className="btn btn-primary" onClick={() => setConfirming(true)}>
                Issue coupon to all employees
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="admin-section-heading-row">
        <h2 className="admin-section-heading">Give a coupon to a single employee</h2>
        <button type="button" className="btn btn-ghost" onClick={() => setShowEmployeeList((s) => !s)}>
          {showEmployeeList ? "Hide" : "Show"}
        </button>
      </div>
      {showEmployeeList && (
        <>
          <input
            type="text"
            className="coach-search"
            placeholder="Search by email…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search wallets by email"
          />
          {walletsError ? (
            <div className="state-panel">
              <p>{walletsError}</p>
            </div>
          ) : walletsLoading ? (
            <div className="state-panel">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="state-panel">
              <p>No wallets match.</p>
            </div>
          ) : (
            <ul className="about-admin-list">
              {filtered.map((w) => (
                <WalletCouponRow key={w.id} wallet={w} onGiven={refreshCoupons} />
              ))}
            </ul>
          )}
        </>
      )}

      <h2 className="admin-section-heading">Coupons issued</h2>
      {couponsError ? (
        <div className="state-panel">
          <p>{couponsError}</p>
        </div>
      ) : couponsLoading ? (
        <div className="state-panel">Loading…</div>
      ) : coupons.length === 0 ? (
        <div className="state-panel">
          <p>No coupons issued yet.</p>
        </div>
      ) : (
        <ul className="about-admin-list">
          {coupons.map((c) => (
            <IssuedCouponRow key={c.id} coupon={c} onChanged={refreshCoupons} />
          ))}
        </ul>
      )}
    </>
  );
}

function transactionTypeLabel(type: CafeteriaTransaction["type"]): string {
  return type === "topup" ? "Top-up" : type === "topup_all" ? "Top-up (all)" : type === "coupon" ? "Coupon" : "Charge";
}

// A coupon redemption never touches the wallet (amount is always 0 — the employee isn't
// charged), but the company still paid for the item; show that real cost here instead of
// a misleading "+0.00 EGP".
function couponFaceValue(tx: CafeteriaTransaction): number {
  return (tx.items ?? []).reduce((sum, item) => sum + item.price * item.quantity, 0);
}

// items is populated for both "charge" and "coupon" transactions — a top-up has none.
function itemsSummary(tx: CafeteriaTransaction): string {
  if (!tx.items || tx.items.length === 0) return "—";
  return tx.items.map((i) => `${i.quantity}× ${i.name}`).join(", ");
}

function TransactionRow({ tx, onDeleted }: { tx: CafeteriaTransaction; onDeleted: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleDelete() {
    setDeleting(true);
    setError(null);
    try {
      await cafeteriaClient.deleteTransaction(tx.id);
      onDeleted();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't delete this transaction.");
      setDeleting(false);
      setConfirming(false);
    }
  }

  return (
    <tr>
      <td>{new Date(tx.createdAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</td>
      <td>{transactionTypeLabel(tx.type)}</td>
      <td>{tx.walletEmail}</td>
      <td>{itemsSummary(tx)}</td>
      {tx.type === "coupon" ? (
        <td className="cafeteria-amount--debit">
          −{formatEgp(couponFaceValue(tx))} <span className="field-hint">(comped, not charged)</span>
        </td>
      ) : (
        <td className={tx.amount >= 0 ? "cafeteria-amount--credit" : "cafeteria-amount--debit"}>
          {tx.amount >= 0 ? "+" : ""}
          {formatEgp(tx.amount)}
        </td>
      )}
      <td>{formatEgp(tx.balanceAfter)}</td>
      <td>{tx.performedByUserId}</td>
      <td className="cafeteria-transactions-table-actions">
        {confirming ? (
          <div className="cancel-confirm cancel-confirm--stacked">
            <span>Reverses {formatEgp(-tx.amount)}. Sure?</span>
            <button type="button" className="btn btn-ghost" onClick={() => setConfirming(false)} disabled={deleting}>
              No
            </button>
            <button type="button" className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? "Deleting…" : "Yes"}
            </button>
          </div>
        ) : (
          <button type="button" className="btn btn-danger-ghost" onClick={() => setConfirming(true)}>
            Delete
          </button>
        )}
        {error && <div className="field-error">{error}</div>}
      </td>
    </tr>
  );
}

function BatchTransactionRow({
  batchId,
  sample,
  count,
  totalAmount,
  onDeleted,
}: {
  batchId: string;
  sample: CafeteriaTransaction;
  count: number;
  totalAmount: number;
  onDeleted: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleDelete() {
    setDeleting(true);
    setError(null);
    try {
      await cafeteriaClient.deleteTransactionBatch(batchId);
      onDeleted();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't delete this top-up.");
      setDeleting(false);
      setConfirming(false);
    }
  }

  return (
    <tr>
      <td>{new Date(sample.createdAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</td>
      <td>{transactionTypeLabel(sample.type)}</td>
      <td>{count} employees</td>
      <td>—</td>
      <td className={sample.amount >= 0 ? "cafeteria-amount--credit" : "cafeteria-amount--debit"}>
        {sample.amount >= 0 ? "+" : ""}
        {formatEgp(sample.amount)} each ({formatEgp(totalAmount)} total)
      </td>
      <td>—</td>
      <td>{sample.performedByUserId}</td>
      <td className="cafeteria-transactions-table-actions">
        {confirming ? (
          <div className="cancel-confirm cancel-confirm--stacked">
            <span>
              Reverses {formatEgp(sample.amount)} from {count} employees. Sure?
            </span>
            <button type="button" className="btn btn-ghost" onClick={() => setConfirming(false)} disabled={deleting}>
              No
            </button>
            <button type="button" className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? "Deleting…" : "Yes"}
            </button>
          </div>
        ) : (
          <button type="button" className="btn btn-danger-ghost" onClick={() => setConfirming(true)}>
            Delete
          </button>
        )}
        {error && <div className="field-error">{error}</div>}
      </td>
    </tr>
  );
}

// Every row a single topUpAll fanned out into shares a batchId — group those into one
// display row so a bulk top-up reads (and deletes) as the single event it actually was.
type TransactionRowItem =
  | { kind: "single"; tx: CafeteriaTransaction }
  | { kind: "batch"; batchId: string; sample: CafeteriaTransaction; count: number; totalAmount: number };

function groupTransactions(transactions: CafeteriaTransaction[]): TransactionRowItem[] {
  const out: TransactionRowItem[] = [];
  const batchIndex = new Map<string, number>();
  for (const tx of transactions) {
    if (!tx.batchId) {
      out.push({ kind: "single", tx });
      continue;
    }
    const idx = batchIndex.get(tx.batchId);
    if (idx === undefined) {
      batchIndex.set(tx.batchId, out.length);
      out.push({ kind: "batch", batchId: tx.batchId, sample: tx, count: 1, totalAmount: tx.amount });
    } else {
      const entry = out[idx];
      if (entry.kind === "batch") {
        entry.count += 1;
        entry.totalAmount += tx.amount;
      }
    }
  }
  return out;
}

function csvEscape(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

// Uses the raw, ungrouped ledger (every per-wallet row of a batch top-up included
// separately) — an export is for record-keeping/analysis, where the on-screen "N
// employees" collapse would just throw away detail someone might need.
function transactionsToCsv(transactions: CafeteriaTransaction[]): string {
  const header = ["Date", "Type", "Employee", "Items", "Amount (EGP)", "Balance after (EGP)", "Performed by", "Note"];
  const lines = [header.join(",")];
  for (const tx of transactions) {
    // Match the same "real cost" convention the table uses: a coupon's ledger amount is
    // always 0 (the employee isn't charged), but the item's menu price is what it actually
    // cost the company.
    const amount = tx.type === "coupon" ? -couponFaceValue(tx) : tx.amount;
    const row = [
      tx.createdAt,
      transactionTypeLabel(tx.type),
      tx.walletEmail,
      itemsSummary(tx),
      amount.toFixed(2),
      tx.balanceAfter.toFixed(2),
      tx.performedByUserId,
      tx.note ?? "",
    ];
    lines.push(row.map((v) => csvEscape(String(v))).join(","));
  }
  return lines.join("\r\n");
}

function downloadTransactionsCsv(transactions: CafeteriaTransaction[]): void {
  // Leading BOM so Excel opens the file as UTF-8 instead of guessing the system codepage —
  // matters here since employee names/notes can include Arabic text.
  const blob = new Blob(["﻿" + transactionsToCsv(transactions)], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `cafeteria-transactions-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function TransactionsSection() {
  const [transactions, setTransactions] = useState<CafeteriaTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  function refresh() {
    setIsLoading(true);
    setError(null);
    cafeteriaClient
      .getAdminTransactions()
      .then(setTransactions)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load transactions."))
      .finally(() => setIsLoading(false));
  }

  useEffect(refresh, []);

  const rows = useMemo(() => groupTransactions(transactions), [transactions]);

  if (error) {
    return (
      <div className="state-panel">
        <p>{error}</p>
      </div>
    );
  }
  if (isLoading) {
    return <div className="state-panel">Loading…</div>;
  }
  if (transactions.length === 0) {
    return (
      <div className="state-panel">
        <p>No transactions yet.</p>
      </div>
    );
  }

  return (
    <>
      <div className="cafeteria-transactions-toolbar">
        <button type="button" className="btn btn-ghost" onClick={() => downloadTransactionsCsv(transactions)}>
          Download as CSV
        </button>
      </div>
      <div className="cafeteria-transactions-table-wrap">
        <table className="cafeteria-transactions-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Employee</th>
              <th>Items</th>
              <th>Amount</th>
              <th>Balance after</th>
              <th>Performed by</th>
              <th className="cafeteria-transactions-table-actions"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) =>
              row.kind === "single" ? (
                <TransactionRow key={row.tx.id} tx={row.tx} onDeleted={refresh} />
              ) : (
                <BatchTransactionRow
                  key={row.batchId}
                  batchId={row.batchId}
                  sample={row.sample}
                  count={row.count}
                  totalAmount={row.totalAmount}
                  onDeleted={refresh}
                />
              ),
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function CafeteriaAdminPage() {
  const [tab, setTab] = useState<AdminTab>("menu");

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Manage cafeteria</h1>
          <p className="page-sub">Menu, wallet top-ups, and the transactions ledger.</p>
        </div>
      </header>

      <div className="admin-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={tab === t.id ? "admin-tab-btn is-active" : "admin-tab-btn"}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "menu" && <MenuSection />}
      {tab === "topup" && <TopUpSection />}
      {tab === "coupons" && <CouponSection />}
      {tab === "transactions" && <TransactionsSection />}
      {tab === "summary" && <MonthlySummary />}
    </div>
  );
}
